// The printable invoice.
//
// One component, used by the lab and by the clinic portal, so both print the identical document —
// a doctor holding a copy the lab can't reproduce is how billing arguments start.
//
// Printing goes through the browser (Ctrl+P → Save as PDF) rather than a server-side PDF renderer.
// That means no headless Chromium in the container, the sheet uses the app's real fonts and brand
// colour, and it works on a phone. A server-rendered PDF only becomes necessary when something has
// to ATTACH one without a person present — emailing a statement, say — and that can be added
// without changing this layout.
import { formatDate, formatMoney, type WfInvoice } from '@alignova/shared';
import { AlignovaMark } from './AlignovaLogo.tsx';

/** Amount in words — Pakistani convention, so a printed total can't be quietly altered by a digit. */
function amountInWords(paisa: number): string {
  const rupees = Math.floor(paisa / 100);
  if (rupees === 0) return 'Zero rupees only';
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
    'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

  const under1000 = (n: number): string => {
    if (n === 0) return '';
    if (n < 20) return ones[n]!;
    if (n < 100) return `${tens[Math.floor(n / 10)]}${n % 10 ? `-${ones[n % 10]}` : ''}`;
    return `${ones[Math.floor(n / 100)]} hundred${n % 100 ? ` ${under1000(n % 100)}` : ''}`;
  };

  // Crore / lakh / thousand — the grouping used on invoices here.
  const parts: string[] = [];
  const crore = Math.floor(rupees / 10_000_000);
  const lakh = Math.floor((rupees % 10_000_000) / 100_000);
  const thousand = Math.floor((rupees % 100_000) / 1000);
  const rest = rupees % 1000;
  if (crore) parts.push(`${under1000(crore)} crore`);
  if (lakh) parts.push(`${under1000(lakh)} lakh`);
  if (thousand) parts.push(`${under1000(thousand)} thousand`);
  if (rest) parts.push(under1000(rest));
  const words = parts.join(' ').replace(/\s+/g, ' ').trim();
  return `${words.charAt(0).toUpperCase()}${words.slice(1)} rupees only`;
}

export function InvoicePrint({ invoice, onPrint }: { invoice: WfInvoice; onPrint?: () => void }) {
  const lab = invoice.lab;
  return (
    <>
      {/* Screen-only controls. `no-print` is stripped by the print stylesheet. */}
      <div className="no-print mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => (onPrint ? onPrint() : window.print())}
          className="tap rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-fg"
        >
          Print / Save as PDF
        </button>
        <span className="text-xs text-muted">
          Choose &ldquo;Save as PDF&rdquo; as the destination to keep a copy.
        </span>
      </div>

      <article className="print-sheet mx-auto max-w-[820px] bg-white p-8 text-[13px] leading-relaxed text-slate-900 shadow-level-1 print:shadow-none">
        {/* Letterhead */}
        <header className="flex items-start justify-between gap-6 border-b-2 border-slate-900 pb-5">
          <div className="flex items-start gap-3">
            <AlignovaMark className="h-12 w-12" style={{ color: '#1668A8' }} />
            <div>
              <div className="text-xl font-extrabold tracking-[0.14em]" style={{ color: '#1668A8' }}>
                {(lab?.labName ?? 'ALIGNOVA').toUpperCase()}
              </div>
              <div className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                Clear Aligner Laboratory
              </div>
              {lab?.labAddress && <div className="mt-1 max-w-[42ch] text-[11px] text-slate-600">{lab.labAddress}</div>}
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-slate-600">
                {lab?.labPhone && <span>{lab.labPhone}</span>}
                {lab?.labWhatsapp && <span>WhatsApp {lab.labWhatsapp}</span>}
                {lab?.labEmail && <span>{lab.labEmail}</span>}
              </div>
            </div>
          </div>
          <div className="shrink-0 text-right">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Invoice</div>
            <div className="font-mono text-lg font-bold">{invoice.invoiceNumber}</div>
            <div className="text-[11px] text-slate-600">Issued {formatDate(invoice.issueDate)}</div>
            {invoice.status === 'void' && (
              <div className="mt-1 inline-block rounded border border-red-600 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-red-600">
                Void
              </div>
            )}
          </div>
        </header>

        {/* Who it is for, and what it is for */}
        <section className="mt-5 grid grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Billed to</div>
            <div className="mt-1 font-semibold">{invoice.doctorName ?? invoice.clinicName ?? '-'}</div>
            {invoice.clinicName && invoice.doctorName && <div className="text-slate-600">{invoice.clinicName}</div>}
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">Case</div>
            <div className="mt-1 font-mono">{invoice.caseNumber ?? '-'}</div>
            {invoice.patientName && <div className="text-slate-600">Patient: {invoice.patientName}</div>}
          </div>
        </section>

        {/* Lines */}
        <table className="mt-6 w-full border-collapse">
          <thead>
            <tr className="border-y border-slate-300 bg-slate-50 text-left text-[10px] uppercase tracking-[0.1em] text-slate-600">
              <th className="py-2 pl-2 font-semibold">Description</th>
              <th className="py-2 text-right font-semibold">Qty</th>
              <th className="py-2 text-right font-semibold">Rate</th>
              <th className="py-2 pr-2 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.lines.map((l) => (
              <tr key={l.id} className="border-b border-slate-200">
                <td className="py-2.5 pl-2">{l.description}</td>
                <td className="py-2.5 text-right tabular-nums">{l.quantity}</td>
                <td className="py-2.5 text-right tabular-nums">{formatMoney(l.unitAmount)}</td>
                <td className="py-2.5 pr-2 text-right font-medium tabular-nums">{formatMoney(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2} className="pt-3 align-top text-[11px] text-slate-600">
                <span className="font-semibold uppercase tracking-[0.1em] text-slate-500">In words</span>
                <div className="max-w-[36ch]">{amountInWords(invoice.total)}</div>
              </td>
              <td className="pt-3 text-right font-semibold">Total</td>
              <td className="pt-3 pr-2 text-right text-lg font-bold tabular-nums">{formatMoney(invoice.total)}</td>
            </tr>
          </tfoot>
        </table>

        {invoice.notes && <p className="mt-5 text-[12px] text-slate-700">{invoice.notes}</p>}

        {invoice.status === 'void' && (
          <p className="mt-5 rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            This invoice was voided{invoice.voidedAt ? ` on ${formatDate(invoice.voidedAt)}` : ''}
            {invoice.voidReason ? ` - ${invoice.voidReason}` : ''}. No payment is due against it.
          </p>
        )}

        <footer className="mt-8 border-t border-slate-200 pt-4 text-[11px] text-slate-600">
          <p>
            Payments are credited to your account rather than to a single invoice; your statement in
            the Alignova portal shows the running balance.
          </p>
          <p className="mt-1 text-slate-500">
            {invoice.createdByName ? `Raised by ${invoice.createdByName}. ` : ''}
            This is a computer-generated invoice and is valid without a signature.
          </p>
        </footer>
      </article>
    </>
  );
}
